/**
 * useLatestAlerts — the handful of still-open alerts a DASHBOARD shows at its top, and how
 * many there are in total.
 *
 * The alert board's `useAlertBoard` already owns the same feed, but it owns far more with it:
 * the camera roster, per-camera counters, live WebRTC tiles, preview countdowns, an
 * infinite-scroll card list. A dashboard wants none of that — it wants the newest two or three
 * alerts and a number — so this is the thin half of that hook rather than a second caller of
 * the whole one, and it opens exactly one subscription to do it.
 *
 * Three sources, because they answer three different questions:
 *   • `api.watch()` gives the LIST — the active feed on connect, then every arrival, preview
 *     and lifecycle change as it happens. That feed is capped by the node (100 rows), which is
 *     plenty for a strip showing two cards but useless as a count on a busy machine.
 *   • `api.listActiveAlerts({limit: 1})` gives the COUNT — the node's own total, which is what
 *     the "N open" pill must read. It is fetched on mount and after a wipe, moved by the same
 *     live deltas that move the list, and re-synced whenever the page calls `refresh()` (the
 *     dashboard's existing poll does), so it can't drift far from what the board would show.
 *   • `loadMore()` gives THE REST, one page at a time and only when asked. The strip is a
 *     slider now, so an operator can walk past the live feed into alerts the socket never
 *     sent; this is what fetches them.
 *
 * NOTHING IS FETCHED AHEAD OF BEING LOOKED AT. This hook could have opened with a 60-row
 * page and had every alert in hand — and on the cloud that would mean sixty snapshot blobs
 * pulled through the tunnel to draw three cards, because every card resolves its own preview.
 * So the mount cost stays exactly what it was (one live subscription plus a `limit: 1` count),
 * and pages are pulled one slide ahead of where the operator actually is. The strip renders a
 * single page at a time for the same reason: previews are fetched for the cards on screen and
 * for no others.
 *
 * Every request is guarded three ways, because this hook is driven by a live feed and a poll
 * and now a slider, all of which can fire at once: one in flight at a time per kind (a burst
 * of arrivals can't fan out into a burst of count queries), a generation stamp so a response
 * that lands after a node switch is dropped rather than applied to the wrong node, and a hard
 * `MAX_HELD` ceiling on rows so sliding can't grow the page without bound.
 *
 * Acknowledge/resolve are optimistic: the alert leaves the strip immediately and the request
 * follows. The node echoes an `alert:lifecycle` for the same alert moments later, which the
 * removal below is idempotent against — an alert cleared twice decrements the count once.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { HexAlert, HexAlertsApi } from '../api/contracts';
import { isActiveAlert } from './useAlertBoard';

/** How much of the live feed to hold. The strip renders 2–3; the rest is headroom so that
 *  acknowledging the top cards reveals the next ones without a refetch. */
const FEED_CAP = 20;

/** Rows per paged fetch. Matched to FEED_CAP so the live feed stands in for page 1 exactly
 *  and the first `loadMore()` asks for page 2 — no page that is already in hand. */
const PAGE_SIZE = FEED_CAP;

/** Ceiling on rows held, however far the operator slides. Past this the strip has stopped
 *  being a glance at the newest alerts and the LIST is the right tool; holding hundreds of
 *  rows here only costs memory and preview fetches for cards nobody will reach. */
const MAX_HELD = 60;

/** Alert timestamps arrive as epoch seconds from the node and ms from some callers. */
const ms = (ts?: number): number => (!ts ? 0 : ts < 1e12 ? ts * 1000 : ts);

/** Newest first. Ties keep insertion order, so a live arrival stays on top of an older row
 *  stamped in the same second. */
const byNewest = (a: HexAlert, b: HexAlert) => ms(b.timestamp) - ms(a.timestamp);

/** Identity for dedupe. Legacy streams omit the id, and camera+timestamp is the only other
 *  thing that distinguishes two rows there. */
const keyOf = (a: HexAlert): string => a.id || `${a.cameraId}-${a.timestamp}`;

/**
 * Union of what is held and what just arrived, newest first, capped.
 *
 * The overlap is the point: a paged fetch and the live feed describe the same alerts from two
 * directions, and a page that re-delivers a row the socket already gave us must not undo the
 * preview that landed on it in between — a paged row carries whatever snapshot the node had
 * when the row was written, which for a fresh alert is nothing at all.
 */
function mergeAlerts(held: HexAlert[], incoming: HexAlert[], cap: number): HexAlert[] {
  const out = new Map<string, HexAlert>();
  for (const a of held) out.set(keyOf(a), a);
  for (const a of incoming) {
    const k = keyOf(a);
    const cur = out.get(k);
    out.set(
      k,
      cur
        ? {
            ...cur,
            ...a,
            snapshotUrl: a.snapshotUrl ?? cur.snapshotUrl,
            previewReady: Math.max(cur.previewReady || 0, a.previewReady || 0),
          }
        : a,
    );
  }
  return [...out.values()].sort(byNewest).slice(0, cap);
}

export interface UseLatestAlertsOptions {
  api: HexAlertsApi;
  /** Cloud: no node selected yet — hold the subscription and the count query. */
  blocked?: boolean;
  /** Changes when the selected node changes → re-subscribe and re-count. */
  nodeKey?: string;
}

export interface LatestAlerts {
  /** Active alerts, newest first: the live feed (FEED_CAP) plus whatever `loadMore` has
   *  pulled behind it, up to MAX_HELD. */
  alerts: HexAlert[];
  /** How many alerts are open on the node in total, which is ≥ `alerts.length`. */
  open: number;
  /** True until the first feed lands, so the strip can show placeholders rather than
   *  "no open alerts" — the two must never look the same. */
  loading: boolean;
  acknowledge: (alert: HexAlert) => void;
  resolve: (alert: HexAlert) => void;
  /** Whether the node implements each action at all (the buttons hide when it doesn't). */
  canAcknowledge: boolean;
  canResolve: boolean;
  /** Re-read the open count from the node. The list needs no refresh — it is live. */
  refresh: () => void;
  /**
   * Pull the next page of older alerts, for a slider that has been walked past the live feed.
   *
   * Call it freely: it is a no-op when a page is already in flight, when everything the node
   * says is open is already held, and when `MAX_HELD` is reached. It is NOT called on mount —
   * a dashboard that fetched every open alert to draw three cards would fetch every one of
   * their previews with it.
   */
  loadMore: () => void;
  /** A page is in flight — the slider shows placeholders for the cells it is waiting on. */
  loadingMore: boolean;
  /** There are older alerts the node could still hand over. False once everything open is
   *  held, once the ceiling is hit, and on nodes with no paged endpoint at all. */
  hasMore: boolean;
  /**
   * Bumped on every LIVE change to the feed: an arrival, a lifecycle clear, a node-side wipe.
   *
   * For the figures that sit near this strip and are NOT live. The dashboard's alert counts come
   * from a 30-second poll of an aggregate endpoint, so a socket alert appeared in the strip
   * instantly and stayed uncounted by the card beside it for most of a minute — which reads as
   * the card being broken, not as two different refresh strategies. A page can watch this and
   * re-read whatever it polls when the feed actually moves, instead of waiting for its timer.
   *
   * Deliberately a counter and not the alert: consumers should re-ask their own source rather
   * than try to patch a total they didn't compute.
   */
  revision: number;
  /**
   * Is the feed connected right now — or `undefined` on a host that can't tell.
   *
   * Three states, not two, and the third is the important one: a dashboard must be able to show
   * NO indicator rather than a green one it can't back up. See `HexAlertHandlers.onConnection`.
   */
  live?: boolean;
}

export function useLatestAlerts({ api, blocked = false, nodeKey }: UseLatestAlertsOptions): LatestAlerts {
  const [alerts, setAlerts] = useState<HexAlert[]>([]);
  const [loading, setLoading] = useState(true);
  /** The node's own total, or null when it can't be asked — then the feed length stands in. */
  const [total, setTotal] = useState<number | null>(null);
  /** Alerts already taken off the strip (optimistically or by a lifecycle event), so the
   *  echoed event for the same alert doesn't decrement the count a second time. */
  const clearedIds = useRef<Set<string>>(new Set());
  /** See `revision` on the returned shape. */
  const [revision, setRevision] = useState(0);
  const bump = useCallback(() => setRevision((r) => r + 1), []);
  const [loadingMore, setLoadingMore] = useState(false);

  /**
   * Whether the transport says it is connected — `undefined` until it says anything, which is
   * how a host that never reports gets NO indicator rather than a false one. Reset to
   * `undefined` (not `false`) on every re-subscribe, so a node switch shows "unknown" for the
   * instant before the new socket answers instead of flashing "Offline".
   */
  const [live, setLive] = useState<boolean | undefined>(undefined);

  /**
   * Which subscription these responses belong to. Bumped on every (re)subscribe, and checked
   * before anything is applied: a count or a page for the site the operator just navigated
   * away from would otherwise land on the new site's strip as its own.
   */
  const genRef = useRef(0);
  /** One request of each kind at a time. The count is re-read by a 30s poll AND on every
   *  wipe; pages are asked for by a slider that can be clicked faster than the node answers. */
  const countInFlight = useRef(false);
  const pageInFlight = useRef(false);
  /** Highest page pulled from `listActiveAlerts`. 0 = only the live feed so far. */
  const pageRef = useRef(0);
  /** How many rows the feed handlers may keep. Grows with each page pulled, so a live
   *  arrival's re-slice can't truncate rows the operator has already slid to. */
  const capRef = useRef(FEED_CAP);
  /** Read by `loadMore` without being in its dependency list — a `loadMore` whose identity
   *  changed on every arrival would re-fire the slider's prefetch effect on every arrival. */
  const heldRef = useRef(0);
  const totalRef = useRef<number | null>(null);
  useEffect(() => {
    heldRef.current = alerts.length;
  }, [alerts]);
  useEffect(() => {
    totalRef.current = total;
  }, [total]);

  const refresh = useCallback(() => {
    if (blocked || !api.listActiveAlerts || countInFlight.current) return;
    const gen = genRef.current;
    countInFlight.current = true;
    api
      .listActiveAlerts({ page: 1, limit: 1 })
      .then((p) => {
        if (gen !== genRef.current) return;
        setTotal(Number(p?.total) || 0);
      })
      // Leave the previous count rather than dropping to the feed length: a failed count
      // query is not evidence that the node is quieter than it was.
      .catch(() => {})
      .finally(() => {
        countInFlight.current = false;
      });
  }, [api, blocked]);

  /** See `loadMore` on the returned shape. */
  const loadMore = useCallback(() => {
    if (blocked || !api.listActiveAlerts || pageInFlight.current) return;
    const held = heldRef.current;
    if (held >= MAX_HELD) return;
    // Nothing left to ask for: the node's own total is already in hand.
    if (totalRef.current != null && held >= totalRef.current) return;
    // Ask for the page AFTER what is held, and never the same page twice — `pageRef` is what
    // stops a short live feed (fewer rows than a page) from asking for page 1 forever.
    const page = Math.max(pageRef.current + 1, Math.floor(held / PAGE_SIZE) + 1);
    const gen = genRef.current;
    pageInFlight.current = true;
    setLoadingMore(true);
    api
      .listActiveAlerts({ page, limit: PAGE_SIZE })
      .then((res) => {
        if (gen !== genRef.current) return;
        pageRef.current = Math.max(pageRef.current, res.page || page);
        capRef.current = Math.min(MAX_HELD, Math.max(FEED_CAP, pageRef.current * PAGE_SIZE));
        // The page carries the node's total too — one fewer count query than asking again.
        if (res.total != null) setTotal(Number(res.total) || 0);
        const fresh = (res.data || []).filter(
          (a) => isActiveAlert(a) && !(a.id && clearedIds.current.has(a.id)),
        );
        setAlerts((prev) => mergeAlerts(prev, fresh, capRef.current));
      })
      .catch(() => {})
      .finally(() => {
        pageInFlight.current = false;
        if (gen === genRef.current) setLoadingMore(false);
      });
  }, [api, blocked]);

  /** Take one alert off the strip, at most once. */
  const clear = useCallback((alertId?: string, cameraId?: string) => {
    const key = alertId || cameraId;
    if (!key) return;
    if (clearedIds.current.has(key)) return;
    clearedIds.current.add(key);
    setAlerts((prev) => prev.filter((a) => (alertId ? a.id !== alertId : a.cameraId !== cameraId)));
    setTotal((t) => (t == null ? t : Math.max(0, t - 1)));
    bump();
  }, [bump]);

  useEffect(() => {
    // Any response still in flight belongs to the previous subscription from here on.
    genRef.current += 1;
    const gen = genRef.current;
    pageRef.current = 0;
    capRef.current = FEED_CAP;
    setLoadingMore(false);
    if (blocked) {
      setAlerts([]);
      setTotal(null);
      setLoading(false);
      return;
    }
    setAlerts([]);
    setLoading(true);
    setLive(undefined);
    clearedIds.current = new Set();
    refresh();

    const stop = api.watch({
      // Guarded by the generation stamp like every other callback here: a socket from the
      // previous node tearing down must not report its drop as the new node's state.
      onConnection: (up) => {
        if (gen === genRef.current) setLive(up);
      },
      onSnapshot: (_counters, recent) => {
        const active = recent
          .filter((a) => isActiveAlert(a) && !(a.id && clearedIds.current.has(a.id)))
          .sort(byNewest);
        setAlerts((prev) => {
          // An empty feed is the node saying nothing is open — including the paged rows,
          // which are older than anything the feed would have kept.
          if (active.length === 0) {
            pageRef.current = 0;
            capRef.current = FEED_CAP;
            return [];
          }
          // The snapshot is authoritative for the window it covers — its newest row back to
          // its oldest — so a held row inside that window and absent from it was cleared.
          // Rows OLDER than the window came from a paged fetch the snapshot never looked at,
          // and dropping those would empty the slider on every poll.
          const oldest = ms(active[active.length - 1].timestamp);
          const kept = prev.filter(
            (a) => ms(a.timestamp) < oldest && !(a.id && clearedIds.current.has(a.id)),
          );
          return mergeAlerts(kept, active, capRef.current);
        });
        setLoading(false);
        // Pollers re-snapshot on a timer; a node that can't be counted rides on the feed.
        if (!api.listActiveAlerts) setTotal(recent.filter(isActiveAlert).length);
      },
      onAlert: (a) => {
        if (!isActiveAlert(a)) return;
        // An alert that arrives again after being actioned is a genuinely new row on the
        // same camera — stop suppressing it.
        if (a.id) clearedIds.current.delete(a.id);
        setAlerts((prev) => {
          if (a.id && prev.some((p) => p.id === a.id)) return prev;
          // `capRef`, not FEED_CAP: an operator sitting on page 3 of the slider would
          // otherwise have the rows under them truncated away by an unrelated arrival.
          return [a, ...prev].sort(byNewest).slice(0, capRef.current);
        });
        setTotal((t) => (t == null ? t : t + 1));
        setLoading(false);
        bump();
      },
      onAlertPreview: (p) => {
        if (!p.alertId) return;
        // The alert is already on screen and only its image is new. Bumping `previewReady`
        // is what re-runs a lazy preview resolver that would have been too early to succeed.
        setAlerts((prev) =>
          prev.map((a) =>
            a.id === p.alertId
              ? { ...a, snapshotUrl: p.snapshotUrl ?? a.snapshotUrl, previewReady: (a.previewReady || 0) + 1 }
              : a,
          ),
        );
      },
      onLifecycle: (c) => {
        // Any non-'new' transition (ack/assign/resolve/cancel) takes the alert off the strip.
        if (c.status && !isActiveAlert({ cameraId: c.cameraId, severity: c.severity || 'normal', status: c.status })) {
          clear(c.alertId, c.cameraId);
        }
      },
      onCleared: () => {
        // The node truncated its event store: every row held here is gone, and no per-alert
        // event will say so. Drop the lot and re-read the count; the adapter follows this
        // with a fresh snapshot for whatever is genuinely still open.
        setAlerts([]);
        setTotal(0);
        clearedIds.current = new Set();
        pageRef.current = 0;
        capRef.current = FEED_CAP;
        refresh();
        bump();
      },
    });
    return stop;
  }, [api, nodeKey, blocked, clear, refresh, bump]);

  const acknowledge = useCallback(
    (alert: HexAlert) => {
      if (!api.acknowledgeAlert || !alert.id) return;
      clear(alert.id);
      api.acknowledgeAlert(alert.id).catch(() => {});
    },
    [api, clear],
  );

  const resolve = useCallback(
    (alert: HexAlert) => {
      if (!api.resolveAlert || !alert.id) return;
      clear(alert.id);
      api.resolveAlert(alert.id).catch(() => {});
    },
    [api, clear],
  );

  const open = useMemo(() => Math.max(total ?? 0, alerts.length), [total, alerts.length]);
  const hasMore = !!api.listActiveAlerts && !blocked && alerts.length < MAX_HELD && alerts.length < open;

  return {
    alerts,
    open,
    loading,
    acknowledge,
    resolve,
    canAcknowledge: !!api.acknowledgeAlert,
    canResolve: !!api.resolveAlert,
    refresh,
    loadMore,
    loadingMore,
    hasMore,
    revision,
    live,
  };
}
